import { CANDIDATES } from "./content";

// Visual for the "questions an investor asks" section, in place of a stock photo:
// three candidate sites on one stretch of city, the 100 m score grid underneath,
// and the side-by-side figures that say why one of them wins. Illustrative only,
// and deterministic, so it draws the same on every load.

const W = 420, H = 230, CELL = 20;

// Low → high on a light ground: clay tints for weak cells, brand greens for strong.
const RAMP = ["#f6e4d6", "#f8eedb", "#e6f2ec", "#c6e6d6", "#8fcfb0", "#4fae84"];

function field() {
  const out = [];
  const [a] = CANDIDATES;
  for (let y = 0; y < H / CELL; y++) {
    for (let x = 0; x < W / CELL; x++) {
      const px = x * CELL + CELL / 2, py = y * CELL + CELL / 2;
      let s = 0.42
        + 0.18 * Math.sin(x * 0.45 + 0.7) * Math.cos(y * 0.5 - 0.3)
        + 0.12 * Math.sin((x - y) * 0.3 + 1.9);
      const d = Math.hypot(px - a.x, py - a.y) / CELL;
      s += 0.45 * Math.exp(-(d * d) / 10);
      s = Math.max(0, Math.min(0.999, s));
      out.push({ x, y, i: Math.floor(s * RAMP.length) });
    }
  }
  return out;
}
const CELLS = field();

function Pin({ c, best }) {
  return (
    <g>
      {best && <circle cx={c.x} cy={c.y} r="46" fill="rgba(29,145,96,0.08)" stroke="#1d9160" strokeWidth="1.3" strokeDasharray="4 4" />}
      <g transform={`translate(${c.x - 11} ${c.y - 30})`}>
        <path d="M11 0C4.9 0 0 4.8 0 10.8 0 18.8 11 30 11 30s11-11.2 11-19.2C22 4.8 17.1 0 11 0z"
          fill={best ? "#14543e" : "#64748b"} stroke="#fff" strokeWidth="2" />
        <text x="11" y="14.5" textAnchor="middle" fontSize="11" fontWeight="800" fill="#fff">{c.id}</text>
      </g>
      <g transform={`translate(${c.x + 12} ${c.y - 34})`}>
        <rect width="34" height="18" rx="9" fill="#fff" stroke={best ? "#1d9160" : "#e2e8f0"} />
        <text x="17" y="12.5" textAnchor="middle" fontSize="10" fontWeight="800" fill={best ? "#14543e" : "#475569"}>{c.score}</text>
      </g>
    </g>
  );
}

const ROWS = [
  ["Skor kecocokan", "score"],
  ["Populasi 1 km", "pop"],
  ["Kompetitor", "comps"],
  ["Keramaian", "footfall"],
];

export default function CandidateViz() {
  const best = CANDIDATES.reduce((m, c) => (c.score > m.score ? c : m));

  return (
    <figure className="overflow-hidden rounded-3xl border border-brand-200/70 bg-white shadow-xl"
      aria-label={`Perbandingan tiga kandidat lokasi; kandidat ${best.id} mendapat skor tertinggi`}>
      <div className="flex items-center justify-between gap-3 px-5 pt-5">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-700">Tiga kandidat, satu kota</p>
        <span className="shrink-0 whitespace-nowrap rounded-full bg-[#e6f2ec] px-3 py-1 text-xs font-bold text-brand-700">Kandidat {best.id} unggul</span>
      </div>

      <div className="px-5 pt-4">
        <svg viewBox={`0 0 ${W} ${H}`} className="block w-full rounded-2xl bg-[#f4f7f5]" role="img" aria-hidden="true">
          {CELLS.map(({ x, y, i }) => (
            <rect key={`${x}-${y}`} x={x * CELL + 0.5} y={y * CELL + 0.5} width={CELL - 1} height={CELL - 1} fill={RAMP[i]} />
          ))}
          {/* roads over the grid: an arterial, a cross street, a lane */}
          <path d="M-10 150 C 120 128, 260 96, 430 78" stroke="#fff" strokeWidth="10" fill="none" opacity="0.95" />
          <path d="M238 -10 C 232 80, 226 160, 222 240" stroke="#fff" strokeWidth="6" fill="none" opacity="0.95" />
          <path d="M40 -10 C 70 60, 96 130, 90 240" stroke="#fff" strokeWidth="3.5" fill="none" opacity="0.9" />
          {CANDIDATES.map((c) => <Pin key={c.id} c={c} best={c === best} />)}
        </svg>
      </div>

      <div className="overflow-x-auto px-5 pb-2 pt-4">
        <table className="w-full text-left text-xs tabular-nums sm:text-sm">
          <thead>
            <tr className="text-slate-500">
              <th className="py-1.5 font-medium" scope="col"><span className="sr-only">Ukuran</span></th>
              {CANDIDATES.map((c) => (
                <th key={c.id} scope="col" className={`px-2 py-1.5 text-center font-bold ${c === best ? "rounded-t-lg bg-[#e6f2ec] text-brand-800" : "text-slate-600"}`}>{c.id}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([label, key], r) => (
              <tr key={key} className="border-t border-slate-100">
                <th scope="row" className="py-2 pr-2 font-medium text-slate-600">{label}</th>
                {CANDIDATES.map((c) => (
                  <td key={c.id} className={`px-2 py-2 text-center ${c === best ? `bg-[#e6f2ec] font-bold text-brand-800 ${r === ROWS.length - 1 ? "rounded-b-lg" : ""}` : "text-slate-700"}`}>
                    {c[key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <figcaption className="flex items-center justify-between gap-3 px-5 pb-5 pt-2 text-[11px] text-slate-400">
        <span>Data ilustrasi · <span className="whitespace-nowrap">1 sel = 100 m</span></span>
        <span className="flex shrink-0 items-center gap-1.5">
          rendah
          <span className="flex gap-0.5">{RAMP.map((c) => <span key={c} className="h-2 w-3 rounded-sm" style={{ background: c }} />)}</span>
          tinggi
        </span>
      </figcaption>
    </figure>
  );
}
